<script lang="ts" setup>
import { ref, computed, PropType, watch } from "vue";
import { useUpdateAllowedEmailAddressMutation } from "@/graphql/operations";
import { string } from "yup";
import { useI18n } from "vue-i18n";
import { EditableInput } from "@/components";

const props = defineProps({
  documentSpaceId: {
    type: String,
    required: true,
  },
  allowedEmailAddresses: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  required: {
    type: Boolean,
    default: true,
  },
  crmContactEmail: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{
  "update:valid": [isValid: boolean];
}>();

const { t } = useI18n();

const emailSchema = string()
  .email(t("documentSpace.emailVerification.warning"))
  .required(t("documentSpace.emailVerification.warning"));

const saveError = ref(false);
const { executeMutation: updateAllowedEmail, fetching: savingEmail } =
  useUpdateAllowedEmailAddressMutation();

let initialEmail = props.allowedEmailAddresses?.[0] || "";
const email = ref<string>(initialEmail);
const savedEmail = ref<string>(initialEmail);
const isEnabled = ref<boolean>(props.required || !!initialEmail);
const editMode = ref<boolean>(!initialEmail && props.required);
const errorMessage = ref<string>(
  !initialEmail && props.required
    ? t("documentSpace.emailVerification.warning")
    : "",
);

const isEmailValid = computed(() => {
  if (!email.value || email.value.trim() === "") {
    if (!props.required) {
      errorMessage.value = "";
      return true;
    }
    errorMessage.value = t("documentSpace.emailVerification.warning");
    return false;
  }

  try {
    emailSchema.validateSync(email.value);
    errorMessage.value = "";
    return true;
  } catch (error: any) {
    errorMessage.value = error.message;
    return false;
  }
});

const saveEmail = async () => {
  if (savingEmail.value) return;
  saveError.value = false;
  if (isEmailValid.value && email.value) {
    const result = await updateAllowedEmail({
      id: props.documentSpaceId,
      email: email.value,
    });
    if (result.error || !result.data?.updateDocumentSpace) {
      saveError.value = true;
      return;
    }
    savedEmail.value = email.value;
    editMode.value = false;
    emit("update:valid", true);
  }
};

const validateAndSave = async (value: string) => {
  email.value = value;

  if (isEmailValid.value) {
    await saveEmail();
  }
};

const handleCancel = () => {
  saveError.value = false;
  email.value = savedEmail.value;
  editMode.value = false;
  if (savedEmail.value) {
    errorMessage.value = "";
    emit("update:valid", true);
  } else {
    if (props.required) {
      errorMessage.value = t("documentSpace.emailVerification.warning");
      emit("update:valid", false);
    } else {
      errorMessage.value = "";
      emit("update:valid", true);
    }
  }
};

const enableEmailVerification = async () => {
  isEnabled.value = true;

  // Auto-populate from CRM contact if available and no email is set
  if (!email.value && props.crmContactEmail) {
    email.value = props.crmContactEmail;

    // Validate and save automatically
    if (isEmailValid.value) {
      await saveEmail();
    } else {
      editMode.value = true;
    }
  } else {
    editMode.value = true;
  }
};

const disableEmailVerification = async () => {
  if (props.required || savingEmail.value) return;
  saveError.value = false;
  const result = await updateAllowedEmail({
    id: props.documentSpaceId,
    email: "",
  });
  if (result.error || !result.data?.updateDocumentSpace) {
    saveError.value = true;
    return;
  }
  isEnabled.value = false;
  email.value = "";
  savedEmail.value = "";
  editMode.value = false;
  errorMessage.value = "";
  emit("update:valid", true);
};

const toggleEmailVerification = (event: Event) => {
  (event.currentTarget as HTMLInputElement).checked = isEnabled.value;
  if (savingEmail.value) return;
  if (isEnabled.value) void disableEmailVerification();
  else void enableEmailVerification();
};

// Watch for changes to the required prop
watch(
  () => props.required,
  async (newRequired, oldRequired) => {
    initialEmail = props.allowedEmailAddresses?.[0] || "";
    email.value = initialEmail;
    savedEmail.value = initialEmail;

    // If email verification is no longer required, clear the email
    if (oldRequired && !newRequired) {
      isEnabled.value = false;
      editMode.value = false;
      errorMessage.value = "";
      emit("update:valid", true);
    }

    // If email verification becomes required, enable it
    if (!oldRequired && newRequired) {
      isEnabled.value = true;
      if (!email.value) {
        editMode.value = true;
        errorMessage.value = t("documentSpace.emailVerification.warning");
        emit("update:valid", false);
      }
    }
  },
);

// Emit initial validity state based on whether there's already an email or if email is not required
emit("update:valid", !!initialEmail || !props.required);
</script>

<template>
  <div class="menu_section">
    <div class="menu_title">
      {{ $t("documentSpace.emailVerification.title") }}
    </div>

    <!-- Checkbox for optional email verification -->
    <div v-if="!required" class="checkbox_container">
      <label class="checkbox_label">
        <input
          type="checkbox"
          :checked="isEnabled"
          :disabled="savingEmail"
          @change="toggleEmailVerification"
        />
        {{ $t("documentSpace.emailVerification.enableLabel") }}
      </label>
    </div>

    <p v-if="saveError" role="alert" class="error">{{ $t("error") }}</p>
    <!-- Show email input when required or enabled -->
    <div v-if="isEnabled" :inert="savingEmail" :aria-busy="savingEmail">
      <EditableInput
        v-if="editMode"
        :value="email"
        @submit="validateAndSave"
        @cancel="handleCancel"
      />
      <button
        v-else
        type="button"
        class="email_display"
        :aria-label="$t('documentSpace.emailVerification.label')"
        @click="editMode = true"
      >
        <span>{{
          email || $t("documentSpace.emailVerification.placeholder")
        }}</span>
        <span class="material-symbols-outlined" aria-hidden="true">
          {{ email ? "edit" : "add" }}
        </span>
      </button>
      <p v-if="errorMessage && editMode" class="error">{{ errorMessage }}</p>
    </div>

    <!-- Show disabled email input when optional and not enabled -->
    <div v-else-if="!required" class="email_display disabled">
      <span class="placeholder_text">{{
        $t("documentSpace.emailVerification.placeholder")
      }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.menu_section {
  padding-top: 0.8em;
  padding-bottom: 0.8em;
}

.menu_title {
  color: var(--classic-text-muted, #9b9b9b);
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 0.2em;
  font-size: 0.9em;
}

.email_display {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0.9em;
  border: 1px solid var(--classic-border, #00000024);
  cursor: pointer;
  background: var(--classic-surface, white);

  &:hover {
    background-color: var(--classic-surface-hover, #f7f5f2);
  }

  .material-symbols-outlined {
    font-size: 1rem;
    color: var(--classic-text-muted, #9b9b9b);
  }

  &.disabled {
    background-color: var(--classic-control-disabled, #f5f5f5);
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      background-color: var(--classic-control-disabled, #f5f5f5);
    }

    .placeholder_text {
      color: var(--classic-text-muted, #9b9b9b);
      font-style: italic;
    }
  }
}

.error {
  color: var(--classic-danger, #dc3545);
  font-size: 0.85em;
  margin: 0.3em 0 0 0;
  max-width: 320px;
}

.checkbox_container {
  margin: 0.5em 0;
}

.checkbox_label {
  display: flex;
  align-items: center;
  gap: 0.5em;
  cursor: pointer;
  user-select: none;
}
</style>
